// theme.js
// Configuración de tema personalizado para el dashboard

import { createTheme } from '@mui/material/styles';

export const dashboardTheme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
      light: '#534bae',
      dark: '#000051',
      contrastText: '#fff'
    },
    secondary: {
      main: '#5C6BC0',
      light: '#8e99f3',
      dark: '#26418f',
      contrastText: '#fff'
    },
    success: {
      main: '#4caf50',
      light: '#80e27e',
      dark: '#087f23'
    },
    error: {
      main: '#f44336',
      light: '#ff7961',
      dark: '#ba000d'
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff'
    }
  },
  typography: {
    fontFamily: ['-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem'
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.5rem'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem'
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.875rem'
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6
          }
        }
      }
    }
  }
});

// Colores predefinidos para gráficos
export const chartColors = {
  primary: ['#3F51B5', '#5C6BC0', '#7986CB', '#9FA8DA', '#C5CAE9'],
  blue: ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'],
  green: ['#388e3c', '#66bb6a', '#81c784', '#a5d6a7', '#c8e6c9'],
  purple: ['#7b1fa2', '#ab47bc', '#ba68c8', '#ce93d8', '#e1bee7'],
  orange: ['#f57c00', '#ff9800', '#ffa726', '#ffb74d', '#ffcc80']
};

// Configuración de colores para Payment Methods
export const paymentMethodColors = {
  efectivo: '#E8EAF6',
  tarjeta_credito: '#5C6BC0',
  tarjeta_debito: '#9FA8DA',
  transferencia: '#3F51B5'
};

// Configuración de colores para Currencies
export const currencyColors = {
  MXN: '#9FA8DA',
  USD: '#5C6BC0',
  EUR: '#3F51B5',
  CAD: '#7986CB',
  GBP: '#5E35B1'
};

// Función helper para formatear moneda
export const formatCurrency = (amount, currency = 'USD', locale = 'es-MX') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

// Función helper para formatear porcentaje
export const formatPercentage = (value, decimals = 1) => {
  return `${parseFloat(value).toFixed(decimals)}%`;
};

// Función helper para formatear números grandes
export const formatLargeNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
};

// Configuración de tooltips personalizados para Recharts
export const customTooltipStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
};

// Estilos para animaciones
export const animations = {
  fadeIn: {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' }
    },
    animation: 'fadeIn 0.5s ease-out'
  },
  slideIn: {
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(-20px)' },
      to: { opacity: 1, transform: 'translateX(0)' }
    },
    animation: 'slideIn 0.4s ease-out'
  },
  scaleIn: {
    '@keyframes scaleIn': {
      from: { opacity: 0, transform: 'scale(0.95)' },
      to: { opacity: 1, transform: 'scale(1)' }
    },
    animation: 'scaleIn 0.3s ease-out'
  }
};

// Responsive breakpoints helpers
export const breakpoints = {
  mobile: '@media (max-width: 600px)',
  tablet: '@media (max-width: 960px)',
  desktop: '@media (min-width: 1280px)'
};

// Configuración de grid responsive
export const gridConfig = {
  metrics: {
    xs: 12,
    sm: 6,
    md: 2.4
  },
  charts: {
    xs: 12,
    md: 6,
    lg: 3
  },
  fullWidth: {
    xs: 12
  }
};

export default {
  dashboardTheme,
  chartColors,
  paymentMethodColors,
  currencyColors,
  formatCurrency,
  formatPercentage,
  formatLargeNumber,
  customTooltipStyle,
  animations,
  breakpoints,
  gridConfig
};
